import React, { useRef, useState } from 'react'

import { Menu } from '@headlessui/react'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { XCircleIcon } from '@heroicons/react/24/solid'

import AlertBody from '@/components/Alertsv2/AlertBody'
import LoadingIcon from '@/components/LoadingIcon'
import { useCurrentApplication } from '@/graphql/services/application'
import { useListAlertsQuery } from '@/services/alerts'
import { useGetLoggedUserQuery } from '@/services/user'

export const Alerts = () => {
  const [selectedStatus, setSelectedStatus] = useState('all')
  const [selectedTypes, setSelectedTypes] = useState([
    'bounds',
    'missing-data',
    'suspicious-data',
    'bad-data',
  ])

  const { data: application } = useCurrentApplication()
  const { data: user } = useGetLoggedUserQuery()

  const statusOptions = {
    all: 'Open',
    resolved: 'Resolved',
    dismissed: 'Dismissed',
  }

  const {
    data: alertsResults,
    isLoading: alertsLoading,
    refetch,
    isFetching,
  } = useListAlertsQuery(
    {
      page_size: 200,
      application_id: application?.id,
      status: selectedStatus == 'all' ? 'no-status' : selectedStatus,
      types: selectedTypes,
    },
    {
      skip: !application?.id,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    }
  )

  const alerts = alertsResults?.results?.filter((alert) => {
    const isType = selectedTypes.includes(alert.type)

    if (selectedStatus == 'all') {
      return isType && alert.status == 'no-status'
    }

    return isType && alert.status == selectedStatus && alert.status != 'no-status'
  })

  const loadMoreRef = useRef(null)

  const typeOptions = {
    bounds: 'Bounds',
    'missing-data': 'Missing Data',
    'suspicious-data': 'Suspicious Data',
    'bad-data': 'Bad Data',
  }

  const toggleFilter = (filter) => {
    setSelectedTypes((prev) => {
      const updated = prev.includes(filter)
        ? prev.filter((item) => item !== filter)
        : [...prev, filter]

      const initialOrder = ['bounds', 'missing-data', 'suspicious-data']
      return updated.sort((a, b) => initialOrder.indexOf(a) - initialOrder.indexOf(b))
    })
  }

  if (!application) {
    return <p className='p-4'>Please select an application</p>
  }

  return (
    <div>
      <div className='flex'>
        <Menu
          as='div'
          className='relative inline-block'
        >
          <div className='p-2'>
            <Menu.Button className='inline underline text-gray-700 font-medium'>
              Status{' '}
              <span className='ml-1'>
                <ChevronDownIcon
                  width={20}
                  className='inline right-4 top-4 cursor-pointer'
                />
              </span>
            </Menu.Button>
          </div>
          <Menu.Items className='absolute left-0 mt-2 w-40 bg-white border border-gray-300 rounded-md shadow-lg z-10'>
            {Object.keys(statusOptions)?.map((status, key) => (
              <Menu.Item key={key}>
                {({ active }) => (
                  <div
                    onClick={() => setSelectedStatus(status)}
                    className={`flex items-center px-4 py-2 text-sm cursor-pointer ${
                      active ? 'bg-gray-100' : ''
                    } ${selectedStatus === status ? 'font-bold' : ''}`}
                  >
                    {statusOptions[status]}
                  </div>
                )}
              </Menu.Item>
            ))}
          </Menu.Items>
        </Menu>

        <Menu
          as='div'
          className='relative inline-block'
        >
          <div className='p-2'>
            <Menu.Button className='inline underline text-gray-700 font-medium'>
              Alert Type{' '}
              <span className='ml-1'>
                <ChevronDownIcon
                  width={20}
                  className='inline right-4 top-4 cursor-pointer'
                />
              </span>
            </Menu.Button>
          </div>
          <Menu.Items className='absolute left-0 mt-2 w-40 bg-white border border-gray-300 rounded-md shadow-lg z-10'>
            {Object.keys(typeOptions)?.map((status, key) => (
              <Menu.Item key={key}>
                {({ active }) => (
                  <div
                    onClick={() => toggleFilter(status)}
                    className={`flex items-center px-4 py-2 text-sm cursor-pointer ${
                      active ? 'bg-gray-100' : ''
                    }`}
                  >
                    <input
                      type='checkbox'
                      className='mr-2 border border-sky-400 rounded p-2 disabled:bg-gray-400 cursor-pointer'
                      checked={selectedTypes.includes(status)}
                      readOnly
                    />
                    {typeOptions[status]}
                  </div>
                )}
              </Menu.Item>
            ))}
          </Menu.Items>
        </Menu>
      </div>

      <div className='bg-gray-100 p-2 my-1 flex items-center space-x-2 flex-wrap gap-y-2'>
        <span className='flex items-center gap-1 bg-gray-200 text-gray-700 px-3 py-1 rounded-full text-sm'>
          {statusOptions[selectedStatus]}
        </span>

        {selectedTypes.map((filter, key) => (
          <span
            key={key}
            className='flex items-center gap-1 bg-gray-200 text-gray-700 px-3 py-1 rounded-full text-sm'
          >
            {typeOptions[filter]}
            <button
              className='inline'
              type='button'
              onClick={() => toggleFilter(filter)}
            >
              <XCircleIcon
                width={16}
                fill='#707070'
                className='inline right-4 top-4 cursor-pointer'
              />
            </button>
          </span>
        ))}
      </div>

      <div className='flex flex-col pt-3'>
        <>
          {alertsLoading ? (
            <div className='p-2 flex justify-center'>
              <LoadingIcon />
            </div>
          ) : (
            <>
              {alerts && alerts?.length ? (
                <div className='flex flex-col gap-2 px-3 w-full'>
                  {alerts?.map((alert) => (
                    <AlertBody
                      {...alert}
                      user={user}
                      reset={refetch}
                      key={alert.key || alert.message}
                    />
                  ))}
                </div>
              ) : (
                <div className='p-2 text-center'>
                  <p className='text-sm'>No alerts</p>
                </div>
              )}
            </>
          )}
        </>
        <div
          className='flex justify-center p-2 w-full bg-transparent mt-60'
          ref={loadMoreRef}
        >
          {!alertsLoading && isFetching && (
            <LoadingIcon
              height={40}
              width={40}
            />
          )}
        </div>
      </div>
    </div>
  )
}
